import axiosInstance from '@/libs/axios'
import i18n from '@/libs/i18n'

/**
 * Último recurso para o limite de uso de ecash, usado só quando a configuração do programa não
 * pôde ser carregada (ex.: usuário sem permissão `RewardConfiguration.Read`). Fora esse caso o
 * limite vem sempre de `globalCreditUsageLimitPercentage` ou da tabela de preço da venda.
 * Espelha `RewardSettings.DefaultCreditUsageLimitPercentage` no backend.
 */
const FALLBACK_CREDIT_USAGE_LIMIT_PERCENTAGE = 50

const initialState = {
  rewardBalance: null,
  loadingRewardBalance: false,
  rewardConfiguration: null,
  rewardSummary: null,
  rewardTransactions: [],
  loadingRewardSummary: false,
  loadingRewardTransactions: false,
  currentCustomerId: null,
}

const rewardGetters = {
  rewardBalance: state => state.rewardBalance,
  loadingRewardBalance: state => state.loadingRewardBalance,
  rewardConfiguration: state => state.rewardConfiguration,
  rewardSummary: state => state.rewardSummary,
  rewardTransactions: state => state.rewardTransactions,
  loadingRewardSummary: state => state.loadingRewardSummary,
  loadingRewardTransactions: state => state.loadingRewardTransactions,
  currentCustomerId: state => state.currentCustomerId,

  hasRewardBalance: state => state.rewardBalance?.currentBalance > 0,

  formatRewardBalance: (state, getters, rootState, rootGetters) => balance => {
    if (!balance || balance.currentBalance <= 0) {
      return 'Sem saldo'
    }
    // Usar o filtro global de moeda se disponível
    return (
      rootGetters['common/formatCurrency']?.(balance.currentBalance) ||
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(balance.currentBalance)
    )
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  SET_REWARD_BALANCE(state, balance) {
    state.rewardBalance = balance
  },

  SET_LOADING_REWARD_BALANCE(state, loading) {
    state.loadingRewardBalance = loading
  },

  SET_REWARD_CONFIGURATION(state, configuration) {
    state.rewardConfiguration = configuration
  },

  SET_REWARD_SUMMARY(state, summary) {
    state.rewardSummary = summary
  },

  SET_LOADING_REWARD_SUMMARY(state, loading) {
    state.loadingRewardSummary = loading
  },

  SET_REWARD_TRANSACTIONS(state, transactions) {
    state.rewardTransactions = transactions
  },

  SET_LOADING_REWARD_TRANSACTIONS(state, loading) {
    state.loadingRewardTransactions = loading
  },

  SET_CURRENT_CUSTOMER_ID(state, customerId) {
    state.currentCustomerId = customerId
  },

  CLEAR_REWARD_DATA(state) {
    state.rewardBalance = null
    state.rewardSummary = null
    state.rewardTransactions = []
    state.currentCustomerId = null
  },
}

const actions = {
  async getCustomerRewardBalance({ commit }, customerId) {
    if (!customerId) {
      console.log('Setting reward balance to null')
      commit('SET_REWARD_BALANCE', null)
      return null
    }

    try {
      commit('SET_LOADING_REWARD_BALANCE', true)
      const response = await axiosInstance.get(`/api/reward-balance/customer/${customerId}`)
      console.log(`Fetched reward balance for customerId: ${customerId}`, response.data)
      commit('SET_REWARD_BALANCE', response.data)
      commit('SET_CURRENT_CUSTOMER_ID', customerId)
      return response.data
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erro ao buscar saldo de recompensas:', error)
      }
      commit('SET_REWARD_BALANCE', null)
      return null
    } finally {
      commit('SET_LOADING_REWARD_BALANCE', false)
    }
  },

  async getRewardSummary({ commit }, customerId) {
    if (!customerId) {
      return null
    }

    try {
      commit('SET_LOADING_REWARD_SUMMARY', true)
      const response = await axiosInstance.get(`/api/reward-summary/customer/${customerId}`)
      commit('SET_REWARD_SUMMARY', response.data)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar resumo de recompensas:', error)
      return null
    } finally {
      commit('SET_LOADING_REWARD_SUMMARY', false)
    }
  },

  async getRewardTransactions({ commit }, { customerId, params = {} }) {
    if (!customerId) {
      return { items: [], totalItems: 0 }
    }

    try {
      commit('SET_LOADING_REWARD_TRANSACTIONS', true)
      const response = await axiosInstance.get(`/api/reward-transactions/customer/${customerId}`, {
        params,
      })
      commit('SET_REWARD_TRANSACTIONS', response.data.items || [])
      return response.data
    } catch (error) {
      console.error('Erro ao buscar transações de recompensas:', error)
      return { items: [], totalItems: 0 }
    } finally {
      commit('SET_LOADING_REWARD_TRANSACTIONS', false)
    }
  },

  async getRewardConfiguration({ commit }) {
    try {
      const response = await axiosInstance.get('/api/reward-configuration')
      commit('SET_REWARD_CONFIGURATION', response.data)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar configuração de recompensas:', error)
      commit('SET_REWARD_CONFIGURATION', null)
      return null
    }
  },

  async confirmUseRewardBalance({ getters }, { confirm }) {
    if (!getters.hasRewardBalance) {
      return false
    }

    const confirmed = await confirm({
      title: i18n.t('Usar saldo ecash'),
      text: `Cliente possui saldo de ${getters.formatRewardBalance(
        getters.rewardBalance
      )} em ecash. Deseja usar na compra?`,
      icon: 'question',
      confirmButtonText: i18n.t('Sim, usar saldo'),
      cancelButtonText: i18n.t('Não usar'),
    })

    return confirmed
  },

  async calculateRewardDiscount({ getters, dispatch }, { customerId, saleTotal, priceTableId }) {
    if (!getters.hasRewardBalance) {
      // Se não tem saldo carregado, tenta buscar
      if (customerId) {
        await dispatch('getCustomerRewardBalance', customerId)
      }

      if (!getters.hasRewardBalance) {
        return 0
      }
    }

    // O limite depende da configuração do programa. As telas carregam no mount, mas garantimos
    // aqui: sem ela o cálculo cairia no fallback e liberaria um valor que o backend recusa.
    if (!getters.rewardConfiguration) {
      await dispatch('getRewardConfiguration')
    }

    // Mesma regra do backend (`RewardService.ProcessCreditUsageAsync`): vale o limite global,
    // que a tabela de preço da VENDA (não a do cliente) sobrepõe quando tem configuração própria.
    // `priceTableId` chega como número no PDV/ATM e como string na tela de pedido
    // (`order.priceTableId`), por isso a comparação é normalizada.
    const salePriceTableId = priceTableId == null ? null : Number(priceTableId)
    const priceTableSetting =
      salePriceTableId == null
        ? undefined
        : getters.rewardConfiguration?.priceTableSettings?.find(
          setting => Number(setting.priceTableId) === salePriceTableId
        )
    const usageLimitPercentage =
      priceTableSetting?.creditUsageLimitPercentage ??
      getters.rewardConfiguration?.globalCreditUsageLimitPercentage ??
      FALLBACK_CREDIT_USAGE_LIMIT_PERCENTAGE

    const maxUsage = (saleTotal * usageLimitPercentage) / 100
    return Math.min(getters.rewardBalance.currentBalance, maxUsage)
  },

  clearRewardData({ commit }) {
    console.log('Clearing reward data from store')
    commit('CLEAR_REWARD_DATA')
  },
}

export default {
  namespaced: true,
  state: initialState,
  getters: rewardGetters,
  mutations,
  actions,
}
